import { action, observable, computed, makeObservable } from 'mobx';
import {
    BetGliderPopup,
    GameModalPopup,
    MarketingNotificationsPopup,
    MultipleSlotsPopup,
    XtremePushPopup,
} from './popupTypes';
import { RealityCheckPopupState } from '../popups/RealityCheckPopupState';
import { MarketingNotificationsState } from 'src/domains/players/state/MarketingNotificationsState';
import { Common } from 'src/domains/common/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

// GENERAL POPUPS
export interface PopupHelpAndContactType {
    readonly type: 'helpAndContact';
    readonly email: string;
    readonly textPart1: string;
    readonly textPart2: string;
}

export interface PopupDeleteCardType {
    readonly type: 'popupDeleteCard';
    readonly title: string;
    readonly content: string;
    readonly popUpCard: string;
    readonly agreeAction: () => Promise<void>;
    readonly cancelAction?: () => void;
}

interface PopupRealityCheckType {
    readonly type: 'popupRealityCheck';
}

interface PopupRealityCheckNewValueConfirmationType {
    readonly type: 'popupRealityCheckNewValueConfirmation';
}

export type UserAllPopupsType =
    | PopupHelpAndContactType
    | GameModalPopup
    | BetGliderPopup
    | MultipleSlotsPopup
    | PopupDeleteCardType
    | XtremePushPopup;

type ComputedAllPopupsType =
    | PopupRealityCheckType
    | PopupRealityCheckNewValueConfirmationType
    | MarketingNotificationsPopup;

type AllPopupsType = UserAllPopupsType | ComputedAllPopupsType;

export class PopupState {
    @observable private popupsValue: UserAllPopupsType | null = null;

    public static get = AutoWeakMap.create((common: Common) => new PopupState(common));

    public constructor(private readonly common: Common) {
        makeObservable(this);
    }

    @action public show = (newPopup: UserAllPopupsType): void => {
        this.popupsValue = newPopup;
    };

    @action public hide = (): void => {
        this.popupsValue = null;
    };

    getComputedPopups = (): ComputedAllPopupsType | null => {
        const realityCheckPopupState = RealityCheckPopupState.get(this.common);
        if (realityCheckPopupState.isPopupShouldAppear) {
            return {
                type: 'popupRealityCheck',
            };
        }

        if (realityCheckPopupState.isPopupRealityCheckNewValueConfirmationShouldAppear) {
            return {
                type: 'popupRealityCheckNewValueConfirmation',
            };
        }

        const { isModalAvailable } = MarketingNotificationsState.get(this.common);
        if (isModalAvailable) {
            return new MarketingNotificationsPopup();
        }

        return null;
    };

    @computed public get popups(): AllPopupsType | null {
        const computedPopups = this.getComputedPopups();

        if (computedPopups !== null) {
            return computedPopups;
        }

        return this.popupsValue;
    }

    @computed public get rightPanelAttachedToRightEdge(): boolean {
        const popup = this.popups;
        if (popup === null) {
            return false;
        }

        if ('rightPanelStickyToRight' in popup) {
            //TODO - goal is to remove that condition
            return popup.rightPanelStickyToRight;
        }

        return false;
    }

    // TODO: delete all what is below

    @computed public get isAnyPopup(): boolean {
        if (
            this.popups === null ||
            this.popups.type === 'popupRealityCheck' ||
            this.popups.type === 'popupRealityCheckNewValueConfirmation' ||
            this.popups.type === 'marketingNotificationsPopup'
        ) {
            return false;
        }
        return true;
    }

    @computed public get isPopupDeleteCard(): boolean {
        return this.popupsValue?.type === 'popupDeleteCard';
    }

    @computed public get popupForView(): AllPopupsType | null {
        return this.popups;
    }
}
